import { PhysioLayout } from "@/components/layout/physio-layout";
import { Award, ExternalLink, CheckCircle } from "lucide-react";

export default function AcprSpecialistCareer() {
  return (
    <PhysioLayout title="ACPR Specialist Career Route">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-rose-700 to-rose-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Award className="h-8 w-8 text-rose-300" />
            <div>
              <h2 className="text-xl font-bold">ACPR Specialist Career Route</h2>
              <p className="text-rose-300 text-sm">Association of Chartered Physiotherapists in Rheumatology</p>
            </div>
          </div>
          <p className="text-rose-200 text-sm leading-relaxed">
            The ACPR specialist career route supports physiotherapists who wish to develop as recognised experts in rheumatology physiotherapy through structured CPD, engagement with clinical standards, and active contribution to the specialist community.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">ACPR Membership & Specialist Recognition</h3>
          <div className="space-y-3">
            {[
              { level: "Student / Graduate", desc: "Open to physiotherapy students and newly qualified graduates. Access to ACPR resources, newsletters, and events." },
              { level: "Affiliate Member", desc: "For physiotherapy assistants and those working in rheumatology without full CSP membership." },
              { level: "Full Member", desc: "For CSP-registered physiotherapists with an interest or practice in rheumatology. Access to ACPR CPD events, position statements, and the specialist community." },
              { level: "ACPR Committee / Executive", desc: "Committee membership for those wishing to actively shape the direction of rheumatology physiotherapy at a national level. Elected roles available." },
            ].map((m) => (
              <div key={m.level} className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-100 rounded-xl">
                <span className="text-xs font-bold text-rose-700 bg-rose-100 px-2 py-1 rounded-full shrink-0">{m.level}</span>
                <p className="text-sm text-slate-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
          <a href="https://www.csp.org.uk/networks/physiotherapy-networks/association-chartered-physiotherapists-rheumatology" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-rose-700 hover:text-rose-800 font-medium mt-3">
            <ExternalLink className="h-4 w-4" /> Join ACPR via CSP
          </a>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">ACPR Specialist Development Framework</h3>
          <div className="space-y-2">
            {[
              "Maintain ACPR full membership and engage with ACPR communications and newsletters",
              "Attend at least one ACPR study day or conference per year",
              "Apply ACPR position statements and clinical standards to your practice",
              "Contribute to ACPR consultation responses and position statement development",
              "Share expertise with ACPR colleagues via presentations, case discussions, or online forums",
              "Support the development of junior rheumatology physiotherapists within the ACPR community",
              "Engage with ACPR-BSR collaborative working groups and multidisciplinary standards development",
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Key ACPR Clinical Standards Areas</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Exercise prescription in inflammatory arthritis (EULAR aligned)",
              "Hydrotherapy / aquatic physiotherapy in rheumatic disease",
              "Physiotherapy management of axial spondyloarthritis",
              "Assessment and outcome measures in rheumatology",
              "SLE and connective tissue disease physiotherapy",
              "Occupational therapy and physiotherapy liaison in rheumatology",
              "Telehealth in rheumatology physiotherapy",
              "Paediatric rheumatology physiotherapy standards",
            ].map((area) => (
              <div key={area} className="flex items-start gap-2 text-sm text-slate-700 p-3 bg-slate-50 rounded-xl">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 mt-2" />
                {area}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5">
          <h3 className="font-bold text-slate-900 mb-2">Career Milestones — ACPR Specialist Route</h3>
          <div className="space-y-2">
            {[
              { milestone: "Band 6", action: "Join ACPR, attend first ACPR study day, begin specialist CPD log" },
              { milestone: "Band 7", action: "Active ACPR member, contribute to local/regional ACPR activity, present at ACPR or BSR events" },
              { milestone: "Band 8a", action: "Lead ACPR clinical standard development, contribute to consultation responses, mentor others in ACPR network" },
              { milestone: "Band 8b+", action: "ACPR committee or executive role, national influence on rheumatology physiotherapy standards and practice" },
            ].map((m) => (
              <div key={m.milestone} className="flex items-start gap-3">
                <span className="text-xs font-bold bg-rose-200 text-rose-800 px-2 py-1 rounded-full shrink-0">{m.milestone}</span>
                <p className="text-sm text-slate-700 leading-relaxed">{m.action}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
